<script setup lang="ts">
import type { useEstateListingMap } from '~/composables/estateListingMap/useEstateListingMap'
import type {
  EstateListingEssentialResponse,
  EstateType,
} from '~/types/api/EstateListing'

const props = defineProps<{
  officesEssentialResponse: ReturnType<
    typeof useAsyncData<EstateListingEssentialResponse | null>
  >
  estateType: EstateType
  estateListingMap: ReturnType<typeof useEstateListingMap>
}>()

const mapRef = ref<null | HTMLDivElement>(null)

onMounted(async () => {
  if (!mapRef.value) {
    return
  }

  await props.estateListingMap.loadLibs()

  props.estateListingMap.initMap(mapRef.value)
  props.estateListingMap.setMarkersData(
    props.officesEssentialResponse.data?.value?.data || []
  )
})

watch(
  () => props.officesEssentialResponse.data?.value?.data,
  (offices) => {
    props.estateListingMap.setMarkersData(offices || [])
  }
)
</script>

<template>
  <div class="c-estate-listing-map">
    <div
      v-if="
        officesEssentialResponse.pending && !officesEssentialResponse.data.value
      "
      class="c-estate-listing-map__skeleton"
    />

    <div class="c-estate-listing-map__map">
      <div ref="mapRef" class="sc-map" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/skeleton' as skeleton;

.c-estate-listing-map {
  background-color: #e5e3df; // google maps background color
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  &__skeleton {
    @include skeleton.skeleton;

    background-color: #e5e3df; // google maps background color
    width: 100%;
    height: 100%;
    grid-row: 1 / -1;
    grid-column: 1 / -1;
    z-index: 2;
  }

  &__map {
    width: 100%;
    height: 100%;
    grid-row: 1 / -1;
    grid-column: 1 / -1;

    :deep(.sc-map) {
      width: 100%;
      height: 100%;

      .gm-style {
        iframe + div {
          border: none !important;
        }
      }

      img[src^='https://maps.googleapis.com']
      {
        filter: grayscale(100%);
      }

      .sc-cluster-container {
        cursor: auto;
        height: 0;
        position: absolute;
      }

      .sc-cluster {
        font-family: var(--font-family-primary-base);
        width: 36px;
        height: 36px;
        margin-left: -18px;
        margin-top: -18px;
        border-radius: 36px;
        background-color: var(--color-primary);
        color: var(--color-light);
        font-size: 14px;
        font-weight: 600;
        display: grid;
        place-items: center;
        cursor: pointer;

        &:hover {
          background-color: var(--color-secondary);
          color: var(--color-dark);
        }
      }

      .sc-pin {
        width: 24px;
        height: 24px;
        background-color: var(--color-primary);
        color: var(--color-light);
        font-size: 10px;
        font-weight: 600;
        display: grid;
        place-items: center;
        position: relative;

        &::after {
          content: '';
          width: 8px;
          height: 8px;
          background-color: inherit;
          position: absolute;
          bottom: -4px;
          transform: rotate(45deg);
          z-index: -1;
        }
      }

      /* JavaScript will position this div at the bottom of the popup tip. */
      .sc-popup-container {
        cursor: auto;
        height: 0;
        position: absolute;
      }
    }
  }
}
</style>
